import { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { motion } from 'framer-motion';
import { CheckCircle, XCircle, ArrowRight, Spinner } from '@phosphor-icons/react';
import axios from 'axios';
import { Button } from '../components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PaymentSuccessPage() {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const sessionId = params.get('session_id');
  const [state, setState] = useState('polling'); // polling | success | failed | expired
  const [info, setInfo] = useState(null);
  const attemptsRef = useRef(0);

  const pollOnce = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/payments/status/${sessionId}`, { withCredentials: true });
      setInfo(res.data);
      if (res.data.payment_status === 'paid') {
        setState('success');
        return true;
      }
      if (res.data.status === 'expired') {
        setState('expired');
        return true;
      }
      return false;
    } catch (err) {
      console.error(err);
      return false;
    }
  }, [sessionId]);

  useEffect(() => {
    if (!sessionId) {
      setState('failed');
      return;
    }
    let cancelled = false;
    const tick = async () => {
      if (cancelled) return;
      const done = await pollOnce();
      attemptsRef.current += 1;
      if (done) return;
      if (attemptsRef.current >= 10) {
        if (!cancelled) setState('failed');
        return;
      }
      setTimeout(tick, 2000);
    };
    tick();
    return () => { cancelled = true; };
  }, [sessionId, pollOnce]);

  return (
    <div className="min-h-screen bg-[#050505] flex flex-col items-center justify-center px-6" data-testid="payment-success-page">
      <motion.div
        className="max-w-sm w-full p-6 rounded-3xl bg-[#0a0a0a] border border-white/5 text-center"
        initial={{ opacity: 0, y: 16, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.4 }}
      >
        {state === 'polling' && (
          <>
            <div className="w-16 h-16 mx-auto rounded-full bg-[#0ABAB5]/15 flex items-center justify-center mb-4">
              <Spinner size={28} className="text-[#0ABAB5] animate-spin" />
            </div>
            <p className="text-lg font-semibold text-white">{t('paymentProcessing')}</p>
            <p className="text-xs text-white/45 mt-1">{t('payingWithCard')}</p>
          </>
        )}
        {state === 'success' && (
          <>
            <div className="w-16 h-16 mx-auto rounded-full bg-[#0ABAB5]/15 flex items-center justify-center mb-4">
              <CheckCircle size={32} weight="fill" className="text-[#0ABAB5]" />
            </div>
            <p className="text-xl font-bold text-white">{t('paymentSuccess')}</p>
            <p className="text-sm text-white/55 mt-1">{t('paymentSuccessDesc')}</p>
            {info?.label && <p className="text-xs text-[#0ABAB5] mt-2 font-medium">{info.label}</p>}
          </>
        )}
        {(state === 'failed' || state === 'expired') && (
          <>
            <div className="w-16 h-16 mx-auto rounded-full bg-red-500/15 flex items-center justify-center mb-4">
              <XCircle size={32} weight="fill" className="text-red-400" />
            </div>
            <p className="text-xl font-bold text-white">{t('paymentFailed')}</p>
            <p className="text-sm text-white/55 mt-1">{state === 'expired' ? t('paymentCancelledDesc') : ''}</p>
          </>
        )}
        <Button
          onClick={() => navigate('/')}
          className="cta-button-primary w-full h-12 rounded-xl mt-6 text-sm font-semibold"
          data-testid="payment-home-btn"
        >
          {t('backToHome')}
          <ArrowRight size={16} className="ml-2" />
        </Button>
      </motion.div>
    </div>
  );
}
